import type { Components, Theme } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import { tableRowClasses } from '@mui/material/TableRow';
import { varAlpha } from 'minimal-shared/utils';

// ----------------------------------------------------------------------

const MuiTableContainer: Components<Theme>['MuiTableContainer'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      position: 'relative',
      scrollbarWidth: 'thin',
      scrollbarColor: `${varAlpha(theme.vars.palette.text.disabledChannel, 0.4)} ${varAlpha(
        theme.vars.palette.text.disabledChannel,
        0.08,
      )}`,
    }),
  },
};

// ----------------------------------------------------------------------

const MuiTable: Components<Theme>['MuiTable'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    // Separate borders (spacing 0) let the header cells round their corners; collapsed
    // borders ignore border-radius. Rows stay visually flush since only bottom borders exist.
    root: ({ theme }) => ({
      '--palette-TableCell-border': theme.vars.palette.divider,
      borderCollapse: 'separate',
      borderSpacing: 0,
    }),
  },
};

// ----------------------------------------------------------------------

const MuiTableRow: Components<Theme>['MuiTableRow'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      [`&.${tableRowClasses.selected}`]: {
        backgroundColor: varAlpha(theme.vars.palette.primary.darkChannel, 0.04),
        '&:hover': { backgroundColor: varAlpha(theme.vars.palette.primary.darkChannel, 0.08) },
      },
      // Round the hover fill's outer corners so a hovered row reads as a contained band that
      // mirrors the rounded neutral header (see MuiTableCell.head). MUI paints its default hover
      // on the row element, whose rectangular fill sits behind the cells and would mask any
      // rounded corner — so we clear it and repaint the hover on the cells, where border-radius
      // can clip. Scoped to non-selected rows to leave the selected-row hover tint untouched.
      [`&.${tableRowClasses.hover}:not(.${tableRowClasses.selected}):hover`]: {
        backgroundColor: 'transparent',
        [`& .${tableCellClasses.root}`]: { backgroundColor: theme.vars.palette.action.hover },
        [`& .${tableCellClasses.root}:first-of-type`]: {
          borderTopLeftRadius: theme.shape.borderRadius * 1.5,
          borderBottomLeftRadius: theme.shape.borderRadius * 1.5,
        },
        [`& .${tableCellClasses.root}:last-of-type`]: {
          borderTopRightRadius: theme.shape.borderRadius * 1.5,
          borderBottomRightRadius: theme.shape.borderRadius * 1.5,
        },
      },
      '&:last-of-type': { [`& .${tableCellClasses.root}`]: { borderColor: 'transparent' } },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiTableCell: Components<Theme>['MuiTableCell'] = {
  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({ borderBottomStyle: 'solid', borderBottomColor: theme.vars.palette.divider }),
    // Rounds the neutral header band at its outer corners to match the travel-policy list header. The label uses the
    // body text style so the band, not the type, separates the header from the rows.
    head: ({ theme }) => ({
      ...theme.typography.body2,
      color: theme.vars.palette.text.primary,
      backgroundColor: theme.vars.palette.background.neutral,
      '&:first-of-type': {
        borderTopLeftRadius: theme.shape.borderRadius * 1.5,
        borderBottomLeftRadius: theme.shape.borderRadius * 1.5,
      },
      '&:last-of-type': {
        borderTopRightRadius: theme.shape.borderRadius * 1.5,
        borderBottomRightRadius: theme.shape.borderRadius * 1.5,
      },
    }),
    stickyHeader: ({ theme }) => ({
      backgroundColor: theme.vars.palette.background.paper,
      backgroundImage: `linear-gradient(to bottom, ${theme.vars.palette.background.neutral}, ${theme.vars.palette.background.neutral})`,
    }),
    paddingCheckbox: ({ theme }) => ({ paddingLeft: theme.spacing(1) }),
  },
};

// ----------------------------------------------------------------------

const MuiTablePagination: Components<Theme>['MuiTablePagination'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  // v9 moved back/next icon button props under slotProps.actions.{previousButton,nextButton}.
  defaultProps: {
    slotProps: {
      select: { name: 'table-pagination-select' },
      actions: {
        previousButton: { size: 'small' },
        nextButton: { size: 'small' },
      },
    },
  },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: { width: '100%' },
    toolbar: { height: 64 },
    actions: { marginRight: 8 },
    select: ({ theme }) => ({
      paddingLeft: 8,
      display: 'flex',
      alignItems: 'center',
      '&:focus': { borderRadius: theme.shape.borderRadius },
    }),
    selectIcon: {
      right: 4,
      width: 16,
      height: 16,
      top: 'calc(50% - 8px)',
    },
  },
};

// ----------------------------------------------------------------------

export const table = {
  MuiTable,
  MuiTableRow,
  MuiTableCell,
  MuiTableContainer,
  MuiTablePagination,
};
